import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

export const getAllScoreTypeKey = "getAllScoreType" as const;

export const useGetAllScoreType = () => {
    const query = useQuery({
        queryKey: [getAllScoreTypeKey],
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        queryFn: async () => {
            // Récupérer tous les scoreTypes avec pagination automatique
            let allScoreTypes: any[] = [];
            let page = 1;
            let hasNextPage = true;
            const itemsPerPage = 200;

            while (hasNextPage) {
                const response = await loggedInApiCall.get(
                    `/api/score_types?page=${page}&itemsPerPage=${itemsPerPage}`,
                    {
                        headers: {
                            Accept: "application/json",
                        },
                    },
                );

                // Handle both paginated and non-paginated responses
                const pageData = response.data["hydra:member"] || response.data;
                if (Array.isArray(pageData)) {
                    allScoreTypes = [...allScoreTypes, ...pageData];
                }

                // Vérifier s'il y a une page suivante
                hasNextPage = response.data["hydra:view"]?.["hydra:next"] ? true : false;
                page++;

                // Sécurité : éviter une boucle infinie
                if (page > 10) {
                    break;
                }
            }

            return { data: allScoreTypes };
        },
        select: (response) => {
            const scoreTypes = response.data;
            const data = zodParseWithLog(scoreTypes, ScoreTypeSchema.array(), getAllScoreTypeKey);
            return data;
        },
    });

    return {
        scoreTypes: query.data,
        ...query,
    };
};

export const ScoreTypeKindSchema = z.enum([
    "product",
    "ingredient",
    "packaging",
    "company",
    "transport",
    "distribution",
    "transformation",
]);

const ScoreTypeSchema = z.object({
    type: ScoreTypeKindSchema,
    scoreIds: z.number().array().optional(),
    variationIds: z.number().array().optional(),
    defaultVariationIds: z.number().array().optional(),
    subscoreIds: z.number().array().optional(),
    variationOrder: z.number().array().nullish(),
    actionPlanScoreIds: z.number().array().optional(),
});

export type ScoreType = z.infer<typeof ScoreTypeSchema>;
